import { useDispatch } from "react-redux";
import {
  AnimatedCard,
  AnimatedCardProps,
} from "@/shared/base/components/AnimatedCard/AnimatedCard";
import { Box, Button, Divider, Stack, Typography } from "@mui/material";

import {
  InventoryCardPerks,
  InventoryCardListItem,
} from "./inventoryCard.components";
import { InventoryTag } from "./inventoryCard.helpers";

import { seatMapState } from "@/global/store/data/seatMap";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { getUSCurrency } from "@/global/utils/getUSCurrency";
import { calculateTicketPlusPerksPrice } from "../utils.chooseSeats";
import { findSeatMapSectionIdFromMapping } from "../../../InteractiveSeatMap/utils.seatMap";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";

import type {
  GroupLevelInventory,
  InventoryType,
  SuitLevelV2Inventory,
} from "@/global/store/data/types";
import { throttleTime } from "@/global/utils/utils";

interface Props {
  cardVariant?: AnimatedCardProps["variant"];
  inventory: InventoryType | GroupLevelInventory;
  isSelected?: boolean;
  isDisabled?: boolean;
  isDisabledHoverAnim?: boolean;
  isFooterHidden?: boolean;
  onSelect?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onRender?: (el: HTMLElement) => void;
  style?: AnimatedCardProps["style"];
  hideViewSeatButton?: boolean;
}

export function GroupLevelInventoryCard(props: Props) {
  const dispatch = useDispatch();
  const inventory = props.inventory as GroupLevelInventory;
  const teamConfig = useTeamConfig();
  const { suiteImages } = teamConfig.seatMapConfigs;
  const { sm: smDown } = useCssMediaQuery("down");
  const { inventoryCardSettings } = teamConfig.uiConfigs;

  const totalAllInPriceWithPerks = calculateTicketPlusPerksPrice(inventory);

  const handleViewSeats = (e: any) => {
    e.stopPropagation();

    // making sure it closes the previously opened carousel first
    dispatch(
      seatMapState.actions.set({
        viewId3d: "",
        view2dInventoryId: "",
      })
    );

    // re-opening the carousel with latest state
    throttleTime(100, () => {
      dispatch(
        seatMapState.actions.set({
          viewId3d: `S_${findSeatMapSectionIdFromMapping(
            inventory?.sectionName,
            teamConfig
          )}`,
          view2dInventoryId: inventory?.id,
        })
      );
    });
  };

  return (
    <AnimatedCard
      variant={props?.cardVariant}
      style={props?.style}
      onRender={props?.onRender}
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
      borderRadius={"10px"}
      footerHeight={"70px"}
      isSelected={props?.isSelected}
      isDisabled={props?.isDisabled}
      isDisabledHoverAnim={props?.isDisabledHoverAnim}
      isFooterHidden={props?.isFooterHidden}
      renderBody={() => {
        return (
          <Box onClick={props.onSelect}>
            <Stack
              sx={{
                flexDirection: "row",
                position: "absolute",
                top: 0,
                right: "8px",
                zIndex: 1,
                transform: "translate(0%, -50%) scale(1)",
                overflow: "hidden",
                gap: 1,
              }}
            >
              {inventory.sectionNameTags ? (
                <InventoryTag value={inventory.sectionNameTags} />
              ) : null}
            </Stack>

            <Box p={2} py={2.2}>
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
                width={"100%"}
              >
                <Typography
                  variant="body2"
                  fontWeight={700}
                  color="text.primary"
                  fontSize={smDown ? "0.75rem" : "0.875rem"}
                >
                  {inventory?.displaySectionName
                    ? `Section ${inventory.displaySectionName}`
                    : `Section ${inventory.sectionName}`}
                </Typography>

                {!props.hideViewSeatButton ? (
                  <Button
                    sx={{
                      py: 0.5,
                      px: 1,
                      fontSize: smDown ? "0.75rem" : "0.875rem",
                      lineHeight: 1.3,
                    }}
                    variant="contained"
                    onClick={handleViewSeats}
                  >
                    View Seats
                  </Button>
                ) : null}
              </Stack>
            </Box>

            <Divider />

            <Stack padding={"1rem"}>
              <InventoryCardListItem
                label={inventoryCardSettings?.seatCountLabel || "Seat Count: "}
                value={String(inventory?.seatCount)}
              />

              {/* <InventoryCardPerks inventory={inventory} /> */}

              <InventoryCardListItem
                label={"Price per Ticket"}
                value={getUSCurrency(Number(inventory?.seats?.[0]?.basePrice))}
              />

              <InventoryCardListItem
                label={inventoryCardSettings?.totalPriceLabel || "Total Price:"}
                value={getUSCurrency(totalAllInPriceWithPerks)}
              />
            </Stack>
          </Box>
        );
      }}
      renderFooter={() => {
        return (
          <Box width={"100%"} p={2}>
            <Button
              variant={props.isSelected ? "outlined" : "contained"}
              onClick={props.onSelect}
              sx={{ width: "100%" }}
            >
              {props.isSelected ? "Unselect" : "Select +"}
            </Button>
          </Box>
        );
      }}
    />
  );
}
